<template>
  <div class="video-owner-tabs" data-testid="video-owner-tabs">
    <Tabs
      id="video-owner-tabs"
      :selected-tab="selectedTab"
      @clicked="(e: any) => onTabClick(e.tab.hash as VideoOwnerTab)"
      @changed="(e: any) => onTabChange(e.tab.hash as VideoOwnerTab)"
    >
      <!-- Summary Tab -->
      <Tab id="summary" name="Summary" class="video-owner-tabs__tab">
        <div class="video-owner-tabs__summary">
          <div v-if="description" class="video-owner-tabs__summary-header">
            <div class="video-owner-tabs__summary-actions">
              <div class="video-owner-tabs__summary-actions-left">
                <Button
                  v-if="!summaryContentRef?.editMode"
                  type="link"
                  variant="gray"
                  icon="general.edited"
                  tooltip="Edit summary"
                  data-testid="summary-edit-btn"
                  @click="summaryContentRef?.enterEditMode()"
                />
                <Feedback
                  v-if="showFeedback && !summaryContentRef?.editMode"
                  class="video-owner-tabs__feedback"
                  :feedback="feedbackState"
                  :aiProvider="descriptionProvider"
                  :additionalTrackingProps="isSmartActionDescription ? { smart_action_type: smartActionsStore.actionType } : {}"
                  @feedback="handleFeedback"
                />
              </div>
              <Button
                v-if="!summaryContentRef?.editMode"
                class="video-owner-tabs__export-btn"
                type="solid"
                variant="blue"
                icon="general.generalExport"
                text="Export summary"
                data-testid="summary-export-btn"
                @click="openExportModal"
              />
            </div>
          </div>
          <SummaryContent ref="summaryContentRef" />
        </div>
      </Tab>

      <!-- Comments & Reactions Tab -->
      <Tab id="comments" :hidden="!engagementTabVisible" :name="engagementTabName" class="video-owner-tabs__tab">
        <div class="video-owner-tabs__engagement">
          <div v-if="!reactionsDisabled" class="video-owner-tabs__reactions-bar" :class="{ 'video-owner-tabs__reactions-bar--bordered': commentsEnabled }">
            <span class="reactions-label">Reactions</span>
            <Reactions :id="id" />
          </div>
          <Comments v-if="commentsEnabled" @addedComment="emit('addedComment')" />
        </div>
      </Tab>

      <!-- Chapters Tab -->
      <Tab id="chapters" :hidden="!hasChapters" name="Chapters" class="video-owner-tabs__tab">
        <ChaptersTab />
      </Tab>

      <!-- Transcript Tab -->
      <Tab id="transcript" :hidden="!transcriptionTabEnabled" name="Transcript" class="video-owner-tabs__tab">
        <Transcription />
      </Tab>

      <!-- Analytics Tab -->
      <Tab id="analytics" :hidden="!analyticsTabEnabled" name="Analytics" class="video-owner-tabs__tab">
        <Analytics />
      </Tab>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
type VideoOwnerTab = '#summary' | '#comments' | '#chapters' | '#transcript' | '#analytics';

import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Tab, Tabs, Button } from '@/storybook';

import Comments from '@/modules/viewer/components/item_tabs/comments/Comments.vue';
import Transcription from '@/modules/viewer/components/item_tabs/transcription/Transcription.vue';
import Analytics from '@/modules/viewer/components/item_tabs/analytics/Analytics.vue';
import SummaryContent from '@/modules/viewer/components/SummaryContent.vue';
import ChaptersTab from '@/modules/viewer/components/ChaptersTab.vue';
import Reactions from '@/modules/viewer/components/Reactions.vue';
import Feedback from '@/storybook/templates/smart_actions/Feedback.vue';

import { useViewerItemStore, ItemType } from '@/modules/viewer/store/useViewerItemStore';
import { useAiMetadataStore, AiMetadataStatus } from '@/modules/viewer/store/useAiMetadataStore';
import { useSmartActionsStore, AiSmartActionStatus } from '@/modules/viewer/store/useSmartActionsStore';
import { useTranscriptionStore, ItemTranscriptionStatus } from '@/modules/viewer/store/useTranscriptionStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useTabNavigation } from '@/modules/viewer/composables/useTabNavigation';
import { useActiveTabListener } from '@/modules/viewer/composables/useActiveTab';
import { useEngagementTab } from '@/modules/viewer/composables/useEngagementTab';

const emit = defineEmits<{
  addedComment: [];
}>();

const store = useViewerItemStore();
const aiMetadataStore = useAiMetadataStore();
const smartActionsStore = useSmartActionsStore();
const transcriptionStore = useTranscriptionStore();
const organizationStore = useOrganizationStore();
const { isCurrentItemOwner } = useUser();
const { openShareModalAction } = useItemActions();

const { id, type, commentsEnabled, reactionsDisabled, description } = storeToRefs(store);
const { engagementTabName, engagementTabVisible } = useEngagementTab();
const { hasChapters } = storeToRefs(aiMetadataStore);
const { transcriptionStatus } = storeToRefs(transcriptionStore);
const { trial } = storeToRefs(organizationStore);

const isSmartActionDescription = computed(() => {
  return !!smartActionsStore.actionType && smartActionsStore.actionStatus === AiSmartActionStatus.DONE;
});

const isAiMetadataDescription = computed(() => {
  return !isSmartActionDescription.value && aiMetadataStore.status === AiMetadataStatus.DONE;
});

const descriptionProvider = computed((): string | undefined => {
  if (isSmartActionDescription.value) {
    return smartActionsStore.provider || 'openai';
  }
  if (isAiMetadataDescription.value) {
    return aiMetadataStore.provider || 'sieve';
  }
  return undefined;
});

const feedbackState = computed((): 'positive' | 'negative' | null => {
  if (isSmartActionDescription.value) {
    return smartActionsStore.feedback ?? null;
  }
  if (isAiMetadataDescription.value) {
    return aiMetadataStore.feedback ?? null;
  }
  return null;
});

const showFeedback = computed(() => {
  return isCurrentItemOwner.value && (isSmartActionDescription.value || isAiMetadataDescription.value) && description.value && description.value.length > 0;
});

const handleFeedback = (feedbackType: 'positive' | 'negative' | null) => {
  if (isSmartActionDescription.value) {
    smartActionsStore.setSmartActionFeedback(feedbackType);
  } else if (isAiMetadataDescription.value) {
    aiMetadataStore.setAiMetadataFeedback(feedbackType);
  }
};

const summaryContentRef = ref<InstanceType<typeof SummaryContent> | null>(null);

const transcriptionTabEnabled = computed(() => {
  return (
    type.value === ItemType.VIDEO &&
    (isCurrentItemOwner.value ||
      (transcriptionStatus.value && [ItemTranscriptionStatus.DONE, ItemTranscriptionStatus.EDITED].includes(transcriptionStatus.value)))
  );
});

const analyticsTabEnabled = computed(() => {
  return isCurrentItemOwner.value && (trial?.value === undefined || trial?.value === false);
});

const openExportModal = () => {
  openShareModalAction({ modalProps: { selectedTab: '#export' } });
};

const availableTabs = computed(() => {
  const tabs: VideoOwnerTab[] = ['#summary'];
  if (engagementTabVisible.value) {
    tabs.push('#comments');
  }
  if (hasChapters.value) {
    tabs.push('#chapters');
  }
  if (transcriptionTabEnabled.value) {
    tabs.push('#transcript');
  }
  if (analyticsTabEnabled.value) {
    tabs.push('#analytics');
  }
  return tabs;
});

const { selectedTab, onTabClick, onTabChange, selectTab } = useTabNavigation(availableTabs, '#summary');

// Listen for external tab switch requests (from ButtonViews, EngagementBar, etc.)
const { activeTabOverride } = useActiveTabListener();
watch(activeTabOverride, tab => {
  if (tab) {
    selectTab(tab);
    activeTabOverride.value = null;
  }
});
</script>

<style scoped>
.video-owner-tabs {
  @apply mt-4;
}

.video-owner-tabs__tab {
  @apply mt-3 sm:mt-4;
  min-height: 200px;
}

@media (min-width: 640px) {
  .video-owner-tabs__tab {
    min-height: 300px;
  }
}

.video-owner-tabs__summary {
  @apply relative flex flex-col p-3 sm:p-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.video-owner-tabs__summary-header {
  @apply -m-2 p-2 mb-2 bg-white dark:bg-zinc-900 border-b border-slate-300 dark:border-zinc-600;
}

.video-owner-tabs__summary-actions {
  @apply flex items-center justify-between gap-1 sm:gap-2;
}

.video-owner-tabs__summary-actions-left {
  @apply flex items-center gap-1 sm:gap-2;
}

/* Normalize feedback to match edit/export buttons */
.video-owner-tabs__feedback :deep(.feedback-buttons) {
  gap: 0;
}

.video-owner-tabs__feedback :deep(.button) {
  color: var(--slate-600, #4f596c);
  padding: 0.375rem;
}

.dark .video-owner-tabs__feedback :deep(.button) {
  color: var(--zinc-400, #94a6b8);
}

.video-owner-tabs__engagement {
  @apply relative flex flex-col p-3 sm:p-4 gap-3 sm:gap-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.video-owner-tabs__reactions-bar {
  @apply flex flex-wrap items-center gap-2 sm:gap-3;
}

.video-owner-tabs__reactions-bar--bordered {
  @apply pb-3 border-b border-slate-300 dark:border-zinc-600;
}

.reactions-label {
  @apply text-sm font-medium text-zinc-600 dark:text-zinc-400 mr-1;
}
</style>
